import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const personalLoans = {
    layout: {
        listTitle: 'Owed to me',
        formTitle: 'New receivable',
    },
    list: {
        status: {
            settled: 'Paid off',
            writtenOff: 'Written off',
        },
        defaultPersonName: 'Person',
        loanOfDate: 'Recorded on {{date}}',
        overdueOn: 'Overdue · {{date}}',
        agreedFor: 'Agreed for {{date}}',
        whenPossible: 'Whenever',
        ofAmount: 'of {{amount}}',
        open: 'open',
        lent: 'total',
        emptyTitle: 'No one owes you here',
        emptySubtitle: 'Record what someone owes you — a loan, a sale, a split bill — to track the balance per person, without inflating your spending reports.',
        emptyAction: 'New receivable',
        hero: {
            toReceiveFrom_one: 'To receive from {{count}} person',
            toReceiveFrom_other: 'To receive from {{count}} people',
            overdueAmount: '{{amount}} overdue',
        },
        filters: {
            all: 'All',
            overdue: 'Overdue',
            scheduled: 'Scheduled',
            whenPossible: 'Whenever',
            empty: 'No one in this filter.',
        },
        person: {
            overdueDays_one: 'Overdue by {{count}} day',
            overdueDays_other: 'Overdue by {{count}} days',
            whenPossibleSince: 'Whenever · since {{date}}',
            loanCount_one: '{{count}} receivable',
            loanCount_other: '{{count}} receivables',
        },
        endedSection: 'Ended',
        ended: {
            summary_one: '{{count}} receivable · {{amount}} total',
            summary_other: '{{count}} receivables · {{amount}} total',
        },
    },
    detail: {
        eventType: {
            receipt: 'Payment',
            writeOff: 'Write-off',
            reschedule: 'Agreed date',
        },
        eventRow: '{{type}} · {{date}}',
        eventAgreedFor: 'Agreed for {{date}}',
        agreedDateRemoved: 'Agreed date removed',
        balanceAfter: 'Balance after: {{balance}}',
        excessBooked: 'Surplus of {{amount}} booked as income',
        defaultPersonName: 'Person',
        lentOn: 'Recorded on {{date}}',
        registeredWithoutOutflow: 'no account outflow',
        openBalanceLabel: 'Outstanding',
        settledLabel: 'Paid off',
        writtenOffLabel: 'Written off',
        agreedFor: 'Agreed for {{date}}',
        hero: {
            overdueDays_one: 'Overdue by {{count}} day',
            overdueDays_other: 'Overdue by {{count}} days',
            whenPossible: 'Whenever',
            received: '{{amount}} resolved',
            ofLent: 'of {{amount}} total',
        },
        actions: {
            receipt: 'Received',
            reschedule: 'Reschedule',
        },
        scheduleTitle: 'Payment schedule',
        scheduleEdit: 'Edit',
        scheduleHint: 'Each payment enters cash flow as an expected inflow. Whatever comes in always pays down the oldest first.',
        transactionsTitle: 'Transactions',
        transactionsHint: 'The outflow and payments move cash, but only surplus and write-offs show up in reports.',
        historyTitle: 'History',
        existingReceiptBadge: 'before the app',
        emptyTitle: 'No transactions for this record',
    },
    receipt: {
        confirmButton: 'Confirm payment',
        amountLabel: 'Amount received',
        excessHint: 'Pays down {{principal}} from the balance; {{excess}} is surplus and becomes income.',
        balanceHint: 'Outstanding balance: {{balance}} — can be partial.',
        datePlaceholder: 'Date',
        dateHelper: 'Payment date',
        accountLabel: 'Receiving account',
        accountPlaceholder: 'Select account...',
        excessCategoryLabel: 'Surplus category',
        excessCategoryPlaceholder: 'Select income category...',
        notesPlaceholder: 'Note (optional)',
        existingToggleLabel: 'Received before using Kontto',
        existingToggleHint: 'Pays down the balance without touching any account — the money is already in your starting balances.',
        existingOverBalanceHint: 'Cannot exceed the outstanding balance ({{balance}}).',
    },
    writeOff: {
        confirmButton: 'Write off',
        amountLabel: 'Write-off amount',
        overBalanceHint: 'Greater than the outstanding balance ({{balance}}).',
        hint: 'Becomes an expense on the write-off date, in the chosen category. Doesn\'t touch any account — the money already left when it was recorded.',
        datePlaceholder: 'Date',
        dateHelper: 'Write-off date',
        categoryLabel: 'Write-off category',
        categoryPlaceholder: 'Select expense category...',
        notesPlaceholder: 'Note (optional)',
    },
    schedule: {
        saveButton: 'Save schedule',
        title: 'Payment schedule',
        hint: 'Each expected payment enters projected cash flow. It\'s a reminder, not a binding schedule — you can renegotiate the outstanding tail or go back to "whenever". The total has to match the outstanding balance.',
    },
    edit: {
        saveButton: 'Save changes',
        amountLabel: 'Amount owed',
        hintUntouched: 'Amount, date, and account can still change — nothing received or written off yet.',
        hintLocked: 'Amount, date, and account lock after the first payment or write-off; only notes stay editable.',
        datePlaceholder: 'Date',
        dateHelper: 'Record date',
        accountLabelExisting: 'Default receiving account',
        accountLabelOutflow: 'Outflow account',
        accountPlaceholder: 'Select account...',
        notesPlaceholder: 'Notes (optional)',
    },
    rename: {
        confirmButton: 'Rename',
        label: 'Person\'s name',
        placeholder: 'e.g. Carlos',
        hint: 'Applies to all receivables and transactions for this person.',
    },
    menu: {
        title: 'Receivable',
        editSchedule: 'Edit payment schedule',
        defineSchedule: 'Set payment schedule',
        editLoan: 'Edit receivable',
        renamePerson: 'Rename person',
        writeOff: 'Write off (won\'t collect)',
        delete: 'Delete receivable',
    },
    undoDialog: {
        writeOffTitle: 'Undo write-off',
        receiptTitle: 'Undo payment',
        writeOffMessage: 'Removes the {{amount}} expense and returns it to the outstanding balance.',
        receiptMessage: 'Removes the {{amount}} payment and returns it to the outstanding balance. The account balance is recalculated.',
        receiptMessageWithExcess: 'Removes the {{amount}} payment (including the surplus) and returns it to the outstanding balance. The account balance is recalculated.',
        receiptMessageExisting: 'Removes the {{amount}} payment and returns it to the outstanding balance. No account is affected.',
        confirm: 'Undo',
    },
    deleteDialog: {
        title: 'Delete receivable',
        message: 'Deletes the receivable and ALL linked transactions (outflow, payments, surplus, and write-off). Account balances are recalculated. This action can\'t be undone.',
        confirm: 'Delete all',
    },
    scheduleEditor: {
        modeNone: 'Whenever',
        modeSingle: 'Single date',
        modeSchedule: 'Payment plan',
        noneHint: 'No agreed date. The amount lives only in "Owed to me", outside projected cash flow.',
        pickDate: 'Pick date',
        singleHint: 'The full balance ({{amount}}) is expected on this date.',
        timesLabel: '{{n}}×',
        startingFrom: 'starting',
        split: 'Split',
        addReceipt: 'Add payment',
        sumOf: 'Sum {{sum}} of {{target}}',
        matches: 'Matches the balance',
        missing: 'Missing {{amount}}',
        excess: 'Over by {{amount}}',
    },
    pdf: {
        title: 'Receivable — {{name}}',
        lentAmountLabel: 'Original amount',
        lentOnLabel: 'Recorded on',
        resolvedAmountLabel: 'Resolved (received/written off)',
        outstandingBalanceLabel: 'Open balance',
        statusLabel: 'Status',
        statusOpen: 'Open',
        scheduleSection: 'Receipt schedule',
        historySection: 'History',
        colDate: 'Date',
        colAmount: 'Amount',
        colType: 'Type',
        colBalanceAfter: 'Balance after',
        noSchedule: 'No pending receipts.',
        noHistory: 'No receipts or write-offs recorded.',
        exportError: 'Could not generate the PDF.',
    },
    generated: {
        loanTo: 'Receivable from {{name}}',
        scheduledReceipt: 'Payment from {{name}}',
        receiptFrom: 'Payment from {{name}}',
        excessFrom: 'Surplus from {{name}}',
        writeOff: 'Receivable write-off - {{name}}',
        personFallback: 'Person',
    },
} satisfies DeepPartialStrings<TranslationSchema['personalLoans']>;
